import { useState, useCallback, useEffect } from 'react';
import {
  Stack,
  Title,
  Text,
  Box,
  Button,
  Group,
  Paper,
  Alert,
  Progress,
  Tabs,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconInfoCircle,
  IconUpload,
  IconHeart,
  IconFileUpload,
  IconLink,
  IconPlus,
  IconBrandPinterest,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { useAppUser } from '../../hooks/useAppUser';
import { ImageUpload } from '../Recipe/RecipeImport/ImageUpload';
import { PinterestImport } from '../Recipe/RecipeImport/PinterestImport';
import { handleUsageLimitError } from '../../utils/usageLimitNotifications';

interface RecipeUploadStepProps {
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
}

interface RecipeGroup {
  id: string;
  images: File[];
}

export const RecipeUploadStep = ({ onNext, onBack, isLastStep }: RecipeUploadStepProps) => {
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const pinterestConnected = appUser?.familyProfile?.pinterestConnected ?? false;
  const [activeTab, setActiveTab] = useState<'images' | 'url' | 'pinterest'>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('pinterest') === 'connected' ? 'pinterest' : 'images';
  });
  const [recipeGroups, setRecipeGroups] = useState<RecipeGroup[]>([{ id: '1', images: [] }]);

  // Clean up Pinterest callback param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pinterest') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [urls, setUrls] = useState<string[]>(['']);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);

  const handleImagesSelected = useCallback((recipeId: string, images: File[]) => {
    setRecipeGroups(prev => 
      prev.map(group => 
        group.id === recipeId ? { ...group, images } : group
      )
    );
  }, []);

  const handleImagesCleared = useCallback((recipeId: string) => {
    setRecipeGroups(prev =>
      prev.map(group => 
        group.id === recipeId ? { ...group, images: [] } : group
      )
    );
  }, []);

  const addRecipeGroup = useCallback(() => {
    const newId = Date.now().toString();
    setRecipeGroups(prev => [...prev, { id: newId, images: [] }]);
  }, []);

  const removeRecipeGroup = useCallback((recipeId: string) => {
    setRecipeGroups(prev => prev.filter(group => group.id !== recipeId));
  }, []);

  const handleUrlChange = useCallback((index: number, url: string) => {
    setUrls(prev => prev.map((u, i) => i === index ? url : u));
  }, []);

  const addUrl = useCallback(() => {
    setUrls(prev => [...prev, '']);
  }, []);

  const removeUrl = useCallback((index: number) => {
    setUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUploadRecipes = useCallback(async () => {
    if (!user) return;

    let importPromises: Promise<any>[] = [];
    let recipeCount = 0;

    if (activeTab === 'images') {
      const recipesWithImages = recipeGroups.filter(group => group.images.length > 0);
      
      if (recipesWithImages.length === 0) {
        onNext(); // Skip if no recipes to upload
        return;
      }

      const token = await user.getIdToken();

      // Submit each recipe group as a separate image import request
      recipesWithImages.forEach((group) => {
        const formData = new FormData();
        group.images.forEach((image) => {
          formData.append('images', image);
        });

        const importPromise = fetch(`${process.env.REACT_APP_API_URL}/api/recipes/import/images`, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }).then(async response => {
          if (!response.ok) {
            const data = await response.json();
            
            // Handle 429 usage limit errors with user-friendly notifications
            if (handleUsageLimitError(response, data)) {
              throw new Error('Usage limit exceeded');
            }
            
            throw new Error(data.error || 'Failed to import recipe');
          }
          return response.json();
        });

        importPromises.push(importPromise);
      });

      recipeCount = recipesWithImages.length;

    } else if (activeTab === 'url') {
      const validUrls = urls
        .filter(url => url.trim() !== '')
        .map(url => {
          const trimmed = url.trim();
          return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        });
      
      if (validUrls.length === 0) {
        onNext(); // Skip if no URLs to import
        return;
      }

      // Validate URL formats
      const invalidUrls = validUrls.filter(url => {
        try {
          new URL(url.trim());
          return false;
        } catch {
          return true;
        }
      });

      if (invalidUrls.length > 0) {
        notifications.show({
          title: 'Invalid URLs',
          message: 'Please check that all URLs are valid web addresses',
          color: 'orange',
          autoClose: 7000,
        });
        return;
      }

      const token = await user.getIdToken();

      // Submit each URL as a separate import request
      validUrls.forEach((url) => {
        const importPromise = fetch(`${process.env.REACT_APP_API_URL}/api/recipes/import/url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: url.trim() }),
        }).then(async response => {
          if (!response.ok) {
            const data = await response.json();
            
            // Handle 429 usage limit errors with user-friendly notifications
            if (handleUsageLimitError(response, data)) {
              throw new Error('Usage limit exceeded');
            }
            
            throw new Error(data.error || 'Failed to import recipe from URL');
          }
          return response.json();
        });

        importPromises.push(importPromise);
      });

      recipeCount = validUrls.length;
    }

    setImporting(true);
    setImportProgress(0);
    setUploadedCount(0);

    try {
      // Wait for all imports to complete
      await Promise.all(importPromises.map((promise, index) => 
        promise.then(result => {
          setUploadedCount(prev => {
            const newCount = prev + 1;
            setImportProgress((newCount / recipeCount) * 100);
            return newCount;
          });
          return result;
        })
      ));

      // Show success notification
      notifications.show({
        title: `${recipeCount} recipe import${recipeCount > 1 ? 's' : ''} started`,
        message: `Your recipe${recipeCount > 1 ? 's are' : ' is'} being processed. ${recipeCount > 1 ? 'They' : 'It'} will appear in your cookbook when ready.`,
        color: 'blue',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

    } catch (error: any) {
      console.error('Recipe import error:', error);
      
      // Only show generic error notification if it's not a usage limit error
      // (usage limit errors already show a specific notification)
      if (error.message !== 'Usage limit exceeded') {
        notifications.show({
          title: 'Import failed',
          message: error.message || 'Some recipes failed to import. You can add them to your cookbook later.',
          color: 'orange',
          autoClose: 10000,
        });
      }
    } finally {
      setImporting(false);
      onNext();
    }
  }, [activeTab, recipeGroups, urls, user, onNext]);

  const hasContent = activeTab === 'images'
    ? recipeGroups.some(group => group.images.length > 0)
    : activeTab === 'url'
    ? urls.some(url => url.trim() !== '')
    : false;

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconHeart size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Already have some meals you love?
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Upload your favorite recipes or import from websites to personalize your meal plans
          </Text>
        </Stack>
      </Stack>

      {importing && (
        <Paper p="md" withBorder radius="lg">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text fw={500}>Uploading recipes...</Text>
              <Text size="sm" c="dimmed">
                {uploadedCount} of {recipeGroups.filter(g => g.images.length > 0).length}
              </Text>
            </Group>
            <Progress value={importProgress} color="blue" />
          </Stack>
        </Paper>
      )}

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" radius="lg">
        <Text size="sm" c="blue.7">
          This step is optional. You can always add more recipes to your cookbook later.
        </Text>
      </Alert>

      {/* Tabs for Import Method */}
      <Tabs
        value={activeTab}
        onChange={(tab) => setActiveTab(tab as 'images' | 'url' | 'pinterest')}
        // Tighter tab padding so all three tabs share one row at 320px;
        // .compact-tabs additionally drops the icons below 375px (index.css)
        className="compact-tabs"
        styles={{ tab: { paddingInline: 'clamp(6px, 2.5vw, 12px)' } }}
      >
        <Tabs.List grow>
          <Tabs.Tab value="images" leftSection={<IconFileUpload size={16} />}>
            <Box component="span" visibleFrom="xs">From </Box>Files
          </Tabs.Tab>
          <Tabs.Tab value="url" leftSection={<IconLink size={16} />}>
            <Box component="span" visibleFrom="xs">From </Box>URL
          </Tabs.Tab>
          <Tabs.Tab value="pinterest" leftSection={<IconBrandPinterest size={16} />}>
            <Box component="span" visibleFrom="xs">From </Box>Pinterest
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="images">
          <Stack gap="lg" mt="md">
            {/* Recipe Upload Areas */}
            {recipeGroups.map((group, index) => (
              <Stack key={group.id} gap="md">
                {recipeGroups.length > 1 && (
                  <Group justify="space-between" align="center">
                    <Text fw={500} size="lg">
                      {['First', 'Second', 'Third'][index]} Recipe
                    </Text>
                    <Button
                      variant="subtle"
                      size="xs"
                      color="orange"
                      onClick={() => removeRecipeGroup(group.id)}
                      disabled={importing}
                    >
                      Remove
                    </Button>
                  </Group>
                )}
                
                <ImageUpload
                  onImagesSelected={(images) => handleImagesSelected(group.id, images)}
                  onImagesCleared={() => handleImagesCleared(group.id)}
                  disabled={importing}
                  showTips={index === 0}
                />
              </Stack>
            ))}

            {/* Add Another Recipe Button */}
            {recipeGroups.length < 3 && recipeGroups[0]?.images.length > 0 && !importing && (
              <Group justify="center">
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={addRecipeGroup}
                >
                  Add another recipe
                </Button>
              </Group>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="url">
          <Stack gap="lg" mt="md">
            <Text size="sm" c="dimmed">
              Enter the URLs of recipes you'd like to import. We'll extract the recipe information automatically.
            </Text>
            
            {/* URL Inputs */}
            {urls.map((url, index) => (
              <Stack key={index} gap="sm">
                <Group gap="sm" align="flex-end">
                  <TextInput
                    placeholder="https://example.com/recipe"
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    disabled={importing}
                    style={{ flex: 1 }}
                    label={urls.length > 1 ? `Recipe ${index + 1} URL` : 'Recipe URL'}
                  />
                  {urls.length > 1 && (
                    <Button
                      variant="subtle"
                      size="xs"
                      color="orange"
                      onClick={() => removeUrl(index)}
                      disabled={importing}
                    >
                      Remove
                    </Button>
                  )}
                </Group>
              </Stack>
            ))}

            {/* Add Another URL Button */}
            {urls.length < 3 && urls[0]?.trim() !== '' && !importing && (
              <Group justify="center">
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={addUrl}
                >
                  Add another URL
                </Button>
              </Group>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="pinterest">
          {user && (
            <PinterestImport
              user={user}
              pinterestConnected={pinterestConnected}
              onImportStarted={onNext}
              onClose={onNext}
              active={activeTab === 'pinterest'}
            />
          )}
        </Tabs.Panel>
      </Tabs>

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          disabled={importing}
          color="orange"
        >
          Back
        </Button>

        <Group>
          {hasContent && !importing && (
            <Button
              variant="light"
              size="md"
              leftSection={<IconUpload size={18} />}
              onClick={handleUploadRecipes}
              color="orange"
            >
              Import {activeTab === 'images' 
                ? recipeGroups.filter(g => g.images.length > 0).length 
                : urls.filter(url => url.trim() !== '').length} recipe{((activeTab === 'images' && recipeGroups.filter(g => g.images.length > 0).length !== 1) ||
                (activeTab === 'url' && urls.filter(url => url.trim() !== '').length !== 1)) ? 's' : ''}
            </Button>
          )}
          
          <Button
            size="md"
            rightSection={<IconArrowRight size={18} />}
            onClick={onNext}
            loading={importing}
            color="orange"
            variant={hasContent ? "subtle" : "filled"}
          >
            {hasContent ? 'Skip for now' : (isLastStep ? 'Finish' : 'Continue')}
          </Button>
        </Group>
      </Group>
    </Stack>
  );
};
